import { create } from "zustand";
import { persist } from "zustand/middleware";

/**
 * The in-app card this viewer last opened, and when, so the dashboard's
 * "Start work" hero can show the FlowStack app they used most recently
 * (flowstack#1999).
 *
 * Keyed to the viewer exactly as `last-project` is: the entry carries the
 * `user_id` and `tenant_id` of the session that wrote it, and
 * {@link rememberedAppFor} ignores it for any other session. `useLogout`
 * clears it in `onSettled`. It is a preference, never an authority: the
 * dashboard uses the slug only when `/api/apps` still lists that card for
 * this session.
 *
 * `opened_at` is the browser's clock, compared against a run's
 * `submitted_at` from the server's; the two are close enough for "which did
 * the viewer do last".
 *
 * Written by `InAppCard` when the card navigates — its link, or Open in the
 * project chooser; a chooser cancelled opens nothing and records nothing.
 */
export interface LastApp {
  user_id: string;
  tenant_id: string;
  slug: string;
  /** ISO 8601. */
  opened_at: string;
}

interface LastAppState {
  last: LastApp | null;
  remember: (entry: LastApp) => void;
  clear: () => void;
}

function nonEmptyString(value: unknown): value is string {
  return typeof value === "string" && value !== "";
}

/**
 * `value` as a {@link LastApp}, or null when it is not one. localStorage is
 * user-editable, so a rehydrated entry is checked before it is compared.
 */
export function asLastApp(value: unknown): LastApp | null {
  if (typeof value !== "object" || value === null) return null;
  const { user_id, tenant_id, slug, opened_at } = value as Record<string, unknown>;
  if (!nonEmptyString(user_id) || !nonEmptyString(tenant_id) || !nonEmptyString(slug)) return null;
  if (!nonEmptyString(opened_at) || Number.isNaN(Date.parse(opened_at))) return null;
  return { user_id, tenant_id, slug, opened_at };
}

export const useLastAppStore = create<LastAppState>()(
  persist(
    (set) => ({
      last: null,
      remember: (entry) => set({ last: entry }),
      clear: () => set({ last: null }),
    }),
    {
      name: "flowstack-last-app",
      version: 1,
      // Every rehydration keeps only an entry that parses.
      merge: (persisted, current) => ({
        ...current,
        last: asLastApp((persisted as { last?: unknown } | null)?.last),
      }),
    }
  )
);

/** The card this same viewer opened last, with when (epoch ms); else null. */
export function rememberedAppFor(
  last: LastApp | null,
  session: { user_id: string; tenant_id: string } | null | undefined
): { slug: string; openedAt: number } | null {
  const entry = asLastApp(last);
  if (!entry || !session) return null;
  if (entry.user_id !== session.user_id || entry.tenant_id !== session.tenant_id) return null;
  return { slug: entry.slug, openedAt: Date.parse(entry.opened_at) };
}
